import * as THREE from 'three';
import type { Fencer } from './Fencer';
import type { BoutState, PriorityOwner, TargetZone } from './types';

export type TouchEvent = {
  by: PriorityOwner;
  valid: boolean;
  zone: TargetZone;
  simultaneous: boolean;
};

/**
 * Simplified FIE foil logic:
 * - Valid target = metallic lamé (torso capsule)
 * - Right of way: who initiated attack first keeps priority until parried or off-target
 * - Successful parry transfers priority to defender (riposte window)
 * - Off-target (blade near body but outside lame) halts phrase without point
 * - Simultaneous attacks with equal priority → double touch (both score) only if both valid
 */
export class RulesEngine {
  bout: BoutState;
  private haltTimer = 0;
  private phraseLock = 0;
  private lastEvents: TouchEvent[] = [];

  constructor(targetScore = 5, periodSec = 180) {
    this.bout = {
      playerScore: 0,
      opponentScore: 0,
      targetScore,
      timeLeft: periodSec,
      priority: 'none',
      phase: 'en_garde',
      lastTouchBy: 'none',
      lastTouchValid: false,
    };
  }

  reset(targetScore = 5, periodSec = 180): void {
    this.bout = {
      playerScore: 0,
      opponentScore: 0,
      targetScore,
      timeLeft: periodSec,
      priority: 'none',
      phase: 'fencing',
      lastTouchBy: 'none',
      lastTouchValid: false,
    };
    this.haltTimer = 0;
    this.phraseLock = 0;
  }

  startFencing(): void {
    this.bout.phase = 'fencing';
    this.bout.priority = 'none';
  }

  update(
    dt: number,
    player: Fencer,
    opponent: Fencer,
    freezeClock = false,
  ): TouchEvent | null {
    if (this.bout.phase === 'end') return null;

    if (this.bout.phase === 'halt') {
      this.haltTimer -= dt;
      if (this.haltTimer <= 0) {
        this.reposition(player, opponent);
        this.bout.phase = 'fencing';
        this.bout.priority = 'none';
        player.state.hasPriority = false;
        opponent.state.hasPriority = false;
        player.state.canRiposte = false;
        opponent.state.canRiposte = false;
      }
      return null;
    }

    if (this.bout.phase === 'en_garde') {
      this.bout.phase = 'fencing';
    }

    if (this.bout.phase === 'fencing' && !freezeClock) {
      this.bout.timeLeft = Math.max(0, this.bout.timeLeft - dt);
      if (this.bout.timeLeft <= 0) {
        this.endByTime();
        return null;
      }
    }

    this.phraseLock = Math.max(0, this.phraseLock - dt);

    // Priority from attacks
    this.syncPriority(player, opponent);

    // Parry detection: if defender parries while attacker is lunging and close
    this.checkParries(player, opponent);

    if (this.phraseLock > 0) return null;

    const pHit = this.sampleHit(player, opponent);
    const oHit = this.sampleHit(opponent, player);

    if (!pHit && !oHit) return null;

    // Resolve right of way
    const event = this.resolveTouches(pHit, oHit, player, opponent);
    if (event) {
      this.applyTouch(event, player, opponent);
    }
    return event;
  }

  private syncPriority(player: Fencer, opponent: Fencer): void {
    if (player.isAttacking && !opponent.isAttacking) {
      if (this.bout.priority === 'none' || player.state.hasPriority) {
        this.bout.priority = 'player';
        player.state.hasPriority = true;
        opponent.state.hasPriority = false;
      }
    } else if (opponent.isAttacking && !player.isAttacking) {
      if (this.bout.priority === 'none' || opponent.state.hasPriority) {
        this.bout.priority = 'opponent';
        opponent.state.hasPriority = true;
        player.state.hasPriority = false;
      }
    } else if (player.isAttacking && opponent.isAttacking) {
      // whoever started earlier — approximate by actionT remaining
      if (this.bout.priority === 'none') {
        if (player.state.actionT <= opponent.state.actionT) {
          this.bout.priority = 'player';
          player.state.hasPriority = true;
        } else {
          this.bout.priority = 'opponent';
          opponent.state.hasPriority = true;
        }
      }
    }
  }

  private checkParries(player: Fencer, opponent: Fencer): void {
    const dist = Math.abs(player.state.x - opponent.state.x);
    if (dist > 2.4) return;

    if (player.isParrying && opponent.isAttacking && dist < 2.0) {
      // Player steals priority
      opponent.startAction('recover', 0.25);
      player.state.canRiposte = true;
      player.state.hasPriority = true;
      opponent.state.hasPriority = false;
      this.bout.priority = 'player';
    }
    if (opponent.isParrying && player.isAttacking && dist < 2.0) {
      player.startAction('recover', 0.25);
      opponent.state.canRiposte = true;
      opponent.state.hasPriority = true;
      player.state.hasPriority = false;
      this.bout.priority = 'opponent';
    }
  }

  private sampleHit(
    attacker: Fencer,
    defender: Fencer,
  ): { valid: boolean; zone: TargetZone } | null {
    if (!attacker.isAttacking) return null;
    if (defender.state.invuln > 0) return null;

    // Only score near the extension peak of the attack (more readable)
    const t = attacker.state.actionDur > 0 ? attacker.state.actionT / attacker.state.actionDur : 0;
    if (attacker.state.action === 'lunge' && (t < 0.2 || t > 0.92)) return null;
    if (attacker.state.action === 'fleche' && t > 0.85) return null;

    const tip = attacker.tipWorld;
    const chest = defender.lameCenter;
    // Prefer X-distance on the strip + height band (2.5D friendly)
    const dx = Math.abs(tip.x - chest.x);
    const dy = Math.abs(tip.y - chest.y);
    const dz = Math.abs(tip.z - chest.z);
    const distChest = tip.distanceTo(chest);

    // Valid lame: generous for game feel, still torso-centered
    if (dx < 0.42 && dy < 0.38 && dz < 0.45 && tip.y > 0.95 && tip.y < 1.72) {
      return { valid: true, zone: 'lame' };
    }

    // Off-target body (arms/legs/mask)
    const bodyY = tip.y;
    const lateral = Math.abs(tip.x - defender.state.x);
    if (lateral < 0.45 && bodyY > 0.25 && bodyY < 1.95 && distChest < 0.7) {
      return { valid: false, zone: 'off' };
    }

    // Floor
    if (tip.y < 0.14 && Math.abs(tip.x - defender.state.x) < 1.8) {
      return { valid: false, zone: 'floor' };
    }

    return null;
  }

  private resolveTouches(
    pHit: { valid: boolean; zone: TargetZone } | null,
    oHit: { valid: boolean; zone: TargetZone } | null,
    player: Fencer,
    opponent: Fencer,
  ): TouchEvent | null {
    if (pHit && oHit) {
      // Both land this frame
      if (pHit.valid && oHit.valid) {
        if (this.bout.priority === 'player') {
          return { by: 'player', valid: true, zone: pHit.zone, simultaneous: false };
        }
        if (this.bout.priority === 'opponent') {
          return { by: 'opponent', valid: true, zone: oHit.zone, simultaneous: false };
        }
        // Equal / none → double touch in foil is rare; treat as double for fun arcade feel when true simultaneous
        return { by: 'player', valid: true, zone: 'lame', simultaneous: true };
      }
      if (pHit.valid && !oHit.valid) {
        return { by: 'player', valid: true, zone: pHit.zone, simultaneous: false };
      }
      if (oHit.valid && !pHit.valid) {
        return { by: 'opponent', valid: true, zone: oHit.zone, simultaneous: false };
      }
      // both off-target — halt, no point
      return { by: 'none', valid: false, zone: pHit.zone, simultaneous: false };
    }

    if (pHit) {
      if (pHit.valid) {
        // Need priority or opponent not attacking
        if (
          this.bout.priority === 'player' ||
          this.bout.priority === 'none' ||
          !opponent.isAttacking
        ) {
          return { by: 'player', valid: true, zone: pHit.zone, simultaneous: false };
        }
        return null; // opponent has right of way
      }
      return { by: 'player', valid: false, zone: pHit.zone, simultaneous: false };
    }

    if (oHit) {
      if (oHit.valid) {
        if (
          this.bout.priority === 'opponent' ||
          this.bout.priority === 'none' ||
          !player.isAttacking
        ) {
          return { by: 'opponent', valid: true, zone: oHit.zone, simultaneous: false };
        }
        return null;
      }
      return { by: 'opponent', valid: false, zone: oHit.zone, simultaneous: false };
    }

    return null;
  }

  private applyTouch(ev: TouchEvent, player: Fencer, opponent: Fencer): void {
    this.phraseLock = 0.8;
    this.bout.phase = 'halt';
    this.haltTimer = 1.6;
    this.bout.lastTouchBy = ev.by;
    this.bout.lastTouchValid = ev.valid;

    if (ev.simultaneous && ev.valid) {
      this.bout.playerScore += 1;
      this.bout.opponentScore += 1;
    } else if (ev.valid && ev.by === 'player') {
      this.bout.playerScore += 1;
    } else if (ev.valid && ev.by === 'opponent') {
      this.bout.opponentScore += 1;
    }

    player.state.invuln = 1.2;
    opponent.state.invuln = 1.2;
    player.state.hasPriority = false;
    opponent.state.hasPriority = false;
    player.state.canRiposte = false;
    opponent.state.canRiposte = false;
    player.startAction('idle', 0.1);
    opponent.startAction('idle', 0.1);

    if (
      this.bout.playerScore >= this.bout.targetScore ||
      this.bout.opponentScore >= this.bout.targetScore
    ) {
      this.bout.phase = 'end';
    }

    this.lastEvents.push(ev);
  }

  private reposition(player: Fencer, opponent: Fencer): void {
    player.state.x = -2.2;
    opponent.state.x = 2.2;
    player.state.action = 'idle';
    opponent.state.action = 'idle';
  }

  private endByTime(): void {
    this.bout.phase = 'end';
  }

  formatTime(): string {
    const t = Math.ceil(this.bout.timeLeft);
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  priorityLabel(): string {
    if (this.bout.phase === 'halt') return 'HALT';
    if (this.bout.priority === 'player') return 'PRIORITY · YOU';
    if (this.bout.priority === 'opponent') return 'PRIORITY · OPP';
    return 'PHRASE';
  }
}

export function tipToBoxHit(tip: THREE.Vector3, box: THREE.Box3, pad = 0.05): boolean {
  const e = pad;
  return (
    tip.x >= box.min.x - e &&
    tip.x <= box.max.x + e &&
    tip.y >= box.min.y - e &&
    tip.y <= box.max.y + e &&
    tip.z >= box.min.z - e &&
    tip.z <= box.max.z + e
  );
}
